/**
 * What the Inspector says about whatever is in focus: a call (its twins and
 * answer, in the API's words), a bit (who holds it, on which plane, at which
 * key), or anything that can be picked up (its facts and the verbs it takes
 * part in). Facets carry the caveats the API makes us state plainly, such as
 * a rank sent in a claim being trusted and not stored. Pure: SHELL renders it.
 */

import type {
  Args, CallRecord, Catalogue, Held, InspectorModel, Json, Persona, PlaneStore, RecordStore, Source, Subject, Target, Twins, Verb,
} from '../contracts.ts';
import { hex, label, maskOf, slotsOf } from './masks.ts';
import { planeParts } from './planes.ts';
import { describeOf, occupiersOf, worldRef } from './records.ts';
import { describeRefusal, refusalOf } from './refusal.ts';
import { atOf } from './relay-geom.ts';
import { SECRET } from '../usecases/format.ts';
import { objectOf } from './json.ts';

export interface InspectDeps {
  readonly planes: PlaneStore;
  readonly records: RecordStore;
  readonly catalogue?: Catalogue;
  /** `client.twins`: REST, curl and MCP forms of a call. */
  readonly twins?: (op: string, args: Args, persona?: Persona) => Twins;
  /** `gestures.ring`: every verb where the subject is the source or the target. */
  readonly ring?: (subject: Held | Target) => readonly Verb[];
}

export const RANK_CAVEAT = 'the API trusts a rank sent in a claim; it is not stored';
export const SOFT_CAVEAT = 'a soft hold reserves nothing';

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const DAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const pad = (n: number): string => String(n).padStart(2, '0');

/** "Mon 5 Oct", or "Mon 5 Oct 19h" for an hour of a minute World. */
export const positionLabel = (parametric: number, coordinates: readonly number[]): string => {
  const at = atOf({ parametric, coordinates });
  const d = new Date(`${at.date}T00:00:00Z`);
  const day = `${DAYS[d.getUTCDay()]} ${d.getUTCDate()} ${MONTHS[d.getUTCMonth()]}`;
  return at.hour === undefined ? day : `${day} ${pad(at.hour)}h`;
};

/** A slot as a time: "09:00" on an hour World, "19:30" on a minute World. */
export const slotLabel = (coordinates: readonly number[], slot: number, bits: number): string =>
  (bits === 60 ? `${pad(coordinates[2] ?? 0)}:${pad(slot)}` : `${pad(slot)}:00`);

/** A run of slots as times: "09–10", or "19:30–19:41". */
const runLabel = (coordinates: readonly number[], from: number, to: number, bits: number): string =>
  (from === to ? slotLabel(coordinates, from, bits) : bits === 60
    ? `${slotLabel(coordinates, from, bits)}–${slotLabel(coordinates, to, bits)}`
    : `${pad(from)}–${pad(to)}`);

export const sourceLabel = (s: Source | { kind: 'peer'; sid: string } | { kind: 'unknown' }): string => {
  switch (s.kind) {
    case 'hand': return `your hand (${s.via})`;
    case 'director': return `the Director, step ${s.step}`;
    case 'scenario': return `scenario run ${s.run}, step ${s.step}`;
    case 'auto': return 'the playground, on its own';
    case 'peer': return `a peer's tab (${s.sid}, unverified)`;
    case 'unknown': return 'unknown';
  }
};

const short = (s: string | null | undefined, n = 8): string => (s ? `${s.slice(0, n)}…` : '—');

const inspectCall = (record: CallRecord, deps: InspectDeps): InspectorModel => {
  const op = deps.catalogue?.byId(record.op);
  const answer = record.answer;
  const body = objectOf(record.args.body);
  const facts: [string, string][] = [];
  if (answer) {
    const problem = objectOf(answer.body);
    facts.push(['status', answer.status === 0 ? '0 unreachable' : `${answer.status}${typeof problem.title === 'string' && answer.status >= 400 ? ` ${problem.title}` : ''}`]);
    facts.push(['time', `${answer.ms} ms`]);
  } else {
    facts.push(['status', 'at the door']);
  }
  facts.push(['as', `persona ${record.persona}`], ['via', record.via === 'mcp' ? 'MCP tools/call' : 'REST'], ['source', sourceLabel(record.source)]);
  if (answer?.head) facts.push(['head', short(answer.head)]);
  if (answer?.requestId) facts.push(['request', answer.requestId]);
  if (answer?.rate) facts.push(['rate', `${answer.rate.remaining}/${answer.rate.limit} left this minute`]);
  const refusal = refusalOf(answer?.body);
  if (refusal) {
    facts.push(['refused', refusal.by], ['detail', describeRefusal(record, refusal)]);
    if (refusal.conflictsWith.length > 0) facts.push(['conflicts with', refusal.conflictsWith.join(', ')]);
  } else if (answer && answer.status >= 400) {
    const detail = objectOf(answer.body).detail;
    if (typeof detail === 'string') facts.push(['detail', detail]);
  }

  const facets: { label: string; value: string; caveat?: string }[] = [];
  if (typeof body.rank === 'number') facets.push({ label: 'rank override', value: String(body.rank), caveat: RANK_CAVEAT });
  if (body.soft === true) facets.push({ label: 'soft', value: 'yes', caveat: SOFT_CAVEAT });
  if (op) {
    facets.push({ label: 'operation', value: `${op.method} ${op.path}` });
    facets.push({ label: 'MCP tool', value: op.mcp ? op.id : 'none (x-mcp: false)' });
    if (op.idempotent) facets.push({ label: 'idempotent', value: 'Idempotency-Key, set by the playground' });
  }
  const title = op ? `${record.op} · ${op.method} ${op.path}` : record.op;
  return { subject: { kind: 'call', record }, title, facts, facets, verbs: [], twins: record.twins, answer };
};

const inspectBit = (world: string, key: number, slot: number, deps: InspectDeps): InspectorModel => {
  const subject: Subject = { kind: 'bit', world, key, slot };
  const matter = deps.planes.get(world);
  const pos = matter?.byKey.get(key);
  const target: Target = { kind: 'bits', id: `${world}:${key}:${slot}`, world, data: { key, slot } };
  const verbs = deps.ring ? deps.ring(target) : [];
  if (!matter || !pos) {
    return { subject, title: `${world} · key ${key} · slot ${slot}`, facts: [['key', String(key)], ['slot', String(slot)], ['matter', 'not in view']], facets: [], verbs, twins: null, answer: null };
  }
  const bits = matter.leafSlotBits;
  const bit = 1n << BigInt(slot);
  const occupiers = occupiersOf(deps.records.get(world, 'occupiers'));
  const byId = new Map(occupiers.map((o) => [o.id, o]));
  const where = positionLabel(pos.parametric, pos.coordinates);
  const at = atOf(pos);

  const lit = [...pos.planes].filter(([, mask]) => (mask & bit) !== 0n).map(([plane, mask]) => {
    const { occupierId, role } = planeParts(plane);
    const record = byId.get(occupierId);
    return { plane, occupierId, role, mask, record, name: record?.name ?? `#${occupierId}`, kind: record?.kind ?? matter.kinds.get(occupierId) ?? 'holder' };
  });
  // The run under the pointer: the unbroken stretch of the first lit plane that holds this slot.
  const main = lit.find((l) => l.role === 'held') ?? lit[0];
  const facts: [string, string][] = [];
  let title = `${world} · ${where} · ${slotLabel(pos.coordinates, slot, bits)}`;
  if (main) {
    let from = slot;
    let to = slot;
    while (from > 0 && (main.mask & (1n << BigInt(from - 1))) !== 0n) from -= 1;
    while (to < bits - 1 && (main.mask & (1n << BigInt(to + 1))) !== 0n) to += 1;
    title = `run ${main.name} · ${where} · ${runLabel(pos.coordinates, from, to, bits)}`;
  }
  for (const l of lit) facts.push([l.role, `${hex(l.mask)} [${label(l.mask)}] · plane #${l.occupierId} ${l.name}/${l.role}${l.kind === 'foreign' ? ' (a copy: busy there)' : ''}`]);
  if (lit.length === 0) facts.push(['free', 'nobody holds this slot']);
  facts.push(['offered', `${hex(pos.offered)} [${label(pos.offered)}]`], ['key', String(key)], ['seq', `${matter.seq} · ${short(matter.head)}`]);

  const facets: { label: string; value: string; caveat?: string }[] = [];
  const describe = describeOf(deps.records.get(world, 'describe'));
  for (const l of lit) {
    if (!l.record) continue;
    if (l.record.kind === 'holder' || l.record.kind === 'event') {
      facets.push({ label: `${l.name} rank`, value: String(l.record.rank) });
      facets.push({ label: `${l.name} pinned`, value: l.record.pinned ? 'yes: never displaced' : 'no' });
    }
    if (l.record.holder) facets.push({ label: `${l.name} stands for`, value: l.record.holder });
    if (l.role === 'intent') facets.push({ label: `${l.name} soft`, value: 'intent', caveat: SOFT_CAVEAT });
    if (l.role === 'proposed') facets.push({ label: `${l.name} proposed`, value: 'an ask', caveat: 'a proposal holds nothing until the hand says yes' });
  }
  const seats = describe?.seats ?? matter.seats;
  if (seats > 1) {
    const holders = new Set(lit.filter((l) => l.role === 'held' && l.kind !== 'foreign').map((l) => l.record?.holder ?? l.name)).size;
    facets.push({ label: 'seats', value: `${holders} of ${seats} held`, caveat: 'offered ignores seats; ask the door with explain' });
  }

  const { persona, w } = worldRef(world);
  const positionArgs: Record<string, Json> = { date: at.date, ...(at.hour === undefined ? {} : { hour: at.hour }) };
  const read = main?.record && main.kind !== 'foreign'
    ? { op: 'holdings', args: { w, who: main.record.name, ...positionArgs } }
    : { op: 'offered', args: { w, ...positionArgs } };
  const twins = deps.twins ? deps.twins(read.op, read.args, persona) : null;
  return { subject, title, facts, facets, verbs, twins, answer: null };
};

const inspectNoun = (held: Held, deps: InspectDeps): InspectorModel => {
  const data = objectOf(held.data);
  const facts: [string, string][] = [['kind', held.kind], ['id', held.id]];
  if (held.world) facts.push(['world', held.world]);
  for (const [k, v] of Object.entries(data)) {
    if (v === null || typeof v === 'object') continue;
    // A field that exists to carry a secret is left out; a key anywhere else is redacted below.
    if (/secret|key|token/i.test(k) && typeof v === 'string' && /^((sbk|sbo|whsec|sbv)_|sk-ant-)/.test(v)) continue;
    facts.push([k, typeof v === 'string' ? v : JSON.stringify(v)]);
  }
  const facets: { label: string; value: string; caveat?: string }[] = [];
  if (held.kind === 'occupier' && held.world) {
    const o = occupiersOf(deps.records.get(held.world, 'occupiers')).find((r) => r.id === data.id || r.name === data.name);
    if (o) {
      facets.push({ label: 'kind', value: o.kind }, { label: 'rank', value: String(o.rank) }, { label: 'pinned', value: o.pinned ? 'yes' : 'no' });
      if (o.holder) facets.push({ label: 'stands for', value: o.holder });
      if (o.kind === 'foreign') facets.push({ label: 'copy', value: 'read-only', caveat: 'busy there, not here: it takes nothing from what this World offers' });
    }
  }
  if (typeof data.slots === 'string' || Array.isArray(data.slots)) {
    const mask = maskOf(data.slots);
    facts.push(['slots', `${hex(mask)} [${slotsOf(mask).join(', ')}]`]);
  }
  const verbs = deps.ring ? deps.ring(held) : [];
  return { subject: { kind: 'noun', held }, title: held.label, facts, facets, verbs, twins: null, answer: null };
};

/** Anything shaped like a key or a secret, whatever carried it (§8.1: no key ever reaches a view). */
const redact = (text: string): string => text.replace(SECRET, '(redacted)');

const redacted = (m: InspectorModel): InspectorModel => ({
  ...m,
  title: redact(m.title),
  facts: m.facts.map(([k, v]) => [redact(k), redact(v)] as const),
  facets: m.facets.map((f) => ({ ...f, label: redact(f.label), value: redact(f.value), ...(f.caveat === undefined ? {} : { caveat: redact(f.caveat) }) })),
});

/** The Inspector's model of a subject. */
export const inspect = (subject: Subject, deps: InspectDeps): InspectorModel => {
  switch (subject.kind) {
    case 'call': return redacted(inspectCall(subject.record, deps));
    case 'bit': return redacted(inspectBit(subject.world, subject.key, subject.slot, deps));
    case 'noun': return redacted(inspectNoun(subject.held, deps));
  }
};
